import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * Modal — universal modal primitive.
 *
 * Always renders via createPortal into document.body, permanently escaping
 * any backdrop-filter / transform ancestor that would break position:fixed
 * on WebKit (iOS / Capacitor).
 *
 * Features:
 *  - Body scroll lock while open
 *  - Escape key → onClose
 *  - Mousedown-aware overlay dismiss (only fires when click starts AND ends
 *    on the overlay, preventing accidental close when dragging from inside)
 *
 * @param {object}   props
 * @param {boolean}  props.isOpen           - Show/hide the modal
 * @param {Function} props.onClose          - Called on Escape key
 * @param {ReactNode} props.children        - The modal box content
 * @param {string}   [props.overlayClassName] - Merged over default visual
 *                   classes (bg + z-index). Use to override bg color or z.
 *                   Layout classes (fixed inset-0 flex …) are always kept.
 * @param {Function} [props.onOverlayClick] - If provided, clicking the
 *                   overlay (not the inner box) calls this. Use for
 *                   dismissible modals.
 */
export default function Modal({ isOpen, onClose, children, overlayClassName, onOverlayClick }) {
  const mouseDownOnOverlay = useRef(false);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn(
        // Layout — never changes
        'fixed inset-0 flex items-center justify-center px-4 pt-[max(1rem,var(--sat))] pb-[max(1rem,var(--sab))]',
        // Default visual — overridable via overlayClassName
        'z-modal bg-background/80 backdrop-blur-sm',
        overlayClassName,
      )}
      onMouseDown={(e) => {
        mouseDownOnOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (onOverlayClick && e.target === e.currentTarget && mouseDownOnOverlay.current) {
          onOverlayClick();
        }
        mouseDownOnOverlay.current = false;
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
